import React, { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { bool, func, shape, string } from 'prop-types'
import { DialogOverlay, PersonBlock, Button, TextField, DialogOverlayContent, useStatusOverlay } from '@fs/zion-ui'
import { postBirthDate } from '../../services/services'

const textWrapper = css`
  margin: auto;
  width: 196px;
  input {
    text-align: center;
  }
`
const dialogPadding = css`
  padding: 0 20px 20px;
`
const promptText = css`
  padding: 24px 6px;
  text-align: center;
  font-size: 18px;
`
const buttonWrapper = css`
  display: flex;
  justify-content: center;
`
export default function MissingBirthDateDialog({ onUpdate, overlay, person, setYearInfo }) {
  const { name, surname, displayName, gender, portraitThumbnailLink, lifespan } = person
  const [t] = useTranslation()
  const addYearRef = useRef()
  const [yearQuery, setYearQuery] = useState('')
  const [submitEnabled, setSubmitEnabled] = useState(false)
  const showStatusOverlay = useStatusOverlay()

  const validCheck = () => {
    if (
      (yearQuery.length === 4 && yearQuery >= 1000 && yearQuery <= new Date().getFullYear()) ||
      yearQuery.length === 0
    ) {
      if (yearQuery.length === 0) {
        return null
      }
      setSubmitEnabled(true)
      return null
    }
    setSubmitEnabled(false)

    return t('aam.add-year.enter-birth-year', 'Please enter a birth year')
  }

  const setYear = (e) => {
    setYearQuery(e.target.value)
    validCheck()
  }

  const submitBirthYear = () => {
    try {
      postBirthDate({ ...person, birthDate: yearQuery }).then((res) => {
        if (res.status === 200) {
          overlay.close()
          person.dob = yearQuery
          onUpdate(person)
          setYearInfo && setYearInfo(yearQuery)
          setYearQuery('')
        }
      })
    } catch (err) {
      console.error(err)
      showStatusOverlay({
        message: t('aam.add-year.error', 'An error has occured'),
        type: 'error',
      })
    }
  }

  return (
    <DialogOverlay {...overlay} autoWidth headingText={t('aam.add-year.add-birth', 'Add Birth Year')}>
      <div css={dialogPadding}>
        <DialogOverlayContent noPadding>
          <PersonBlock
            size="lg"
            centered
            name={displayName ?? ''}
            avatarProps={{
              src: portraitThumbnailLink,
              sex: gender,
              monogram: `${name?.[0] ?? ''}${surname?.[0] ?? ''}`,
            }}
            details={`${lifespan}`}
          />
          <div css={promptText}>
            {t('aam.add-year.add', 'Looks like we are missing a birth year for this person. Would you like to add it?')}
          </div>
          <div style={{ textAlign: 'center', padding: '4px' }}>{t('aam.add-year.add-year', 'Birth Year')}</div>
          <div css={textWrapper}>
            <TextField
              value={yearQuery}
              type="number"
              ref={addYearRef}
              onChange={(e) => setYear(e)}
              warningValidate={validCheck}
            />
          </div>
          <div css={buttonWrapper}>
            <Button onClick={overlay.close}>{t('aam.add-year.cancel', 'Cancel')}</Button>
            <Button type="submit" onClick={submitBirthYear} emphasis="high" disabled={!submitEnabled}>
              {t('aam.add-year.save', 'Save to FamilySearch')}
            </Button>
          </div>
        </DialogOverlayContent>
      </div>
    </DialogOverlay>
  )
}

MissingBirthDateDialog.propTypes = {
  onUpdate: func,
  overlay: shape({
    autoFocus: bool,
    close: func,
    focusBackRef: shape({}),
    handleClick: func,
    handleTransitioned: func,
    isOpen: bool,
    open: func,
    targetRef: shape({}),
    transition: bool,
  }),
  person: shape({
    atn: string,
    displayName: string,
    gender: string,
    genderType: string,
    lifespan: string,
    living: bool,
    name: string,
    personId: string,
    surname: string,
  }),
}
